import { useEffect, useRef, useState } from 'react'
import { WEEK_HEADINGS, addMonths, longDayLabel, monthGrid, monthLabel, monthOf, moveInMonth } from '@kronos/domain'
import { CalendarHeader } from './CalendarHeader'
import type { GridDay, CalendarKey, LocalDate, LocalMonth } from '@kronos/domain'
import { useDragSelect } from './useDragSelect'

/**
 * One month on a wall calendar, Monday first — the shape people already plan
 * their days in, rather than a list of cards they have to count down.
 *
 * It draws the frame and nothing else: the month buttons, the week headings,
 * today, the weekend tint, the dimmed past, and one button per day. **What a
 * day says is the caller's** (`renderDay`), so availability and hours share
 * one calendar and differ only in what is written in the squares.
 *
 * Every day is a real button with a spoken label. The arrow keys move between
 * days (Home and End to the ends of the week, Page Up and Page Down by month),
 * with one tab stop for the whole grid, as a date picker has. A day that
 * cannot be acted on is `aria-disabled` rather than `disabled`, so the arrow
 * keys can still pass over it.
 */
export interface MonthCalendarProps {
  month: LocalMonth
  onMonthChange: (month: LocalMonth) => void
  today: LocalDate
  minMonth?: LocalMonth | undefined
  maxMonth?: LocalMonth | undefined
  /** What a screen reader calls the grid: "Availability", "Hours". */
  label: string
  renderDay: (day: GridDay) => React.ReactNode
  /** Said after the date, e.g. "available 09:00 to 17:00". */
  describeDay?: (day: GridDay) => string
  /** Extra classes on a day's button, for its state. */
  dayClass?: (day: GridDay) => string
  /** Extra classes on a day's cell, for lines drawn between cells. */
  cellClass?: (day: GridDay) => string
  isDisabled?: (day: GridDay) => boolean
  /** When present, every day says whether it is in it (`aria-pressed`). */
  selection?: ReadonlySet<LocalDate> | undefined
  /** The day the caller is showing below the grid, if any. */
  current?: LocalDate | null
  onDayPress: (date: LocalDate, how: { shiftKey: boolean }) => void
  /** Mouse drag across days. Absent: no drag. */
  onDragSelect?: ((dates: readonly LocalDate[]) => void) | undefined
  /** Buttons at the right of the month header. */
  actions?: React.ReactNode
}

const KEYS: Readonly<Record<string, CalendarKey>> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
  Home: 'weekStart',
  End: 'weekEnd',
}

export function MonthCalendar(props: MonthCalendarProps): JSX.Element {
  const { month, onMonthChange, today, minMonth, maxMonth, selection, current } = props
  const [focus, setFocus] = useState<LocalDate | null>(null)
  const moveFocus = useRef(false)
  const grid = useRef<HTMLDivElement>(null)
  const drag = useDragSelect(props.onDragSelect)

  const weeks = monthGrid(month)
  const thisMonth = monthOf(today)
  const canBack = !minMonth || addMonths(month, -1) >= minMonth
  const canForward = !maxMonth || addMonths(month, 1) <= maxMonth
  // The one day in the tab order: the last focused, else the day on show, else today, else the 1st.
  const tabStop = [focus, current ?? null, today].find((d) => d && monthOf(d) === month) ?? (`${month}-01` as LocalDate)

  useEffect(() => {
    if (!moveFocus.current) return
    moveFocus.current = false
    grid.current?.querySelector<HTMLButtonElement>(`[data-date="${tabStop}"]`)?.focus()
  }, [tabStop])

  const go = (to: LocalMonth, focusDay?: LocalDate): void => {
    onMonthChange(to)
    if (focusDay) {
      moveFocus.current = true
      setFocus(focusDay)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent, date: LocalDate): void => {
    const key = KEYS[e.key]
    if (key) {
      e.preventDefault()
      moveFocus.current = true
      setFocus(moveInMonth(date, key))
    } else if ((e.key === 'PageUp' && canBack) || (e.key === 'PageDown' && canForward)) {
      e.preventDefault()
      const to = addMonths(month, e.key === 'PageUp' ? -1 : 1)
      go(to, `${to}-01` as LocalDate)
    }
  }

  return (
    <div className="cal">
      <CalendarHeader
        month={month}
        today={today}
        canBack={canBack}
        canForward={canForward}
        showToday={month !== thisMonth && (!minMonth || thisMonth >= minMonth) && (!maxMonth || thisMonth <= maxMonth)}
        onGo={(to) => go(to)}
        actions={props.actions}
      />

      <div className="cal__grid" role="grid" aria-label={`${props.label}, ${monthLabel(month)}`} ref={grid}>
        <div className="cal__row cal__weekdays" role="row">
          {WEEK_HEADINGS.map((h, i) => (
            <div key={h} role="columnheader" className={`cal__wd${i >= 5 ? ' cal__wd--weekend' : ''}`}>
              {h}
            </div>
          ))}
        </div>
        {weeks.map((week) => (
          <div className="cal__row cal__week" role="row" key={week[0]?.date}>
            {week.map((day) => (day.inMonth ? dayCell(day) : <div key={day.date} role="gridcell" className="cal__cell cal__cell--pad" />))}
          </div>
        ))}
      </div>
    </div>
  )

  // A function, not a component: a component declared in here would be a new
  // type on every render, and React would remount all 31 buttons — losing the
  // focus and the drag — each time anything changed.
  function dayCell(day: GridDay): JSX.Element {
    const disabled = props.isDisabled?.(day) ?? false
    const selected = selection?.has(day.date) ?? false
    const isToday = day.date === today
    const extra = props.describeDay?.(day)
    const state = [
      'cal-day',
      isToday ? 'is-today' : '',
      day.date < today ? 'is-past' : '',
      selected ? 'is-selected' : '',
      current === day.date ? 'is-current' : '',
      drag.inRange(day.date) && !disabled ? 'is-dragged' : '',
      props.dayClass?.(day) ?? '',
    ]
    return (
      <div key={day.date} role="gridcell" className={`cal__cell${day.weekend ? ' cal__cell--weekend' : ''} ${props.cellClass?.(day) ?? ''}`}>
        <button
          type="button"
          data-date={day.date}
          className={state.filter(Boolean).join(' ')}
          tabIndex={day.date === tabStop ? 0 : -1}
          aria-disabled={disabled || undefined}
          aria-pressed={selection ? selected : undefined}
          aria-current={isToday ? 'date' : undefined}
          aria-label={[longDayLabel(day.date), isToday ? 'today' : '', extra ?? ''].filter(Boolean).join(', ')}
          onKeyDown={(e) => onKeyDown(e, day.date)}
          onFocus={() => setFocus(day.date)}
          onClick={(e) => {
            if (disabled || drag.swallowed()) return
            props.onDayPress(day.date, { shiftKey: e.shiftKey })
          }}
          {...(disabled ? {} : drag.handlers(day.date))}
        >
          <span className="cal-day__num">{day.day}</span>
          <span className="cal-day__body">{props.renderDay(day)}</span>
        </button>
      </div>
    )
  }
}
